// Verwaltung › Datensicherung und Betrieb (docs/betrieb/SPEZIFIKATION.md §9.4): eine Leseaktion für Verwalter und den
// Assistenten – „Läuft die Datensicherung?“. Keine eigene Seite: Die Oberfläche zeigt die Aktion erst nach ihrem
// Umbau; bis dahin über den Assistenten, die API und `npx tsx scripts/ops/status.ts` (Betreiber).
// Zeitpunkte sind ISO-8601-Texte (UTC), Geschäftstage 'YYYY-MM-DD' – wie in den übrigen Verträgen der Verwaltung.
import { defineContract } from '@shared/contract'
import { z } from 'zod'

const zTimestamp = z.string()

/** ok | warn | critical | unknown – dazu „nicht eingerichtet“ (z. B. ohne Sicherungsdienst, ohne Archiv) */
export const zOperationsState = z.enum(['ok', 'warn', 'critical', 'unknown', 'not_configured'])

export const zOperationsCheck = z.object({
  /** 'wal.archive', 'backup.full', 'backup.verify', 'mail.queue' … (18 Prüfungen) */
  key: z.string(),
  /** deutsch: „Sicherung des Datenbank-Protokolls“ */
  label: z.string(),
  state: zOperationsState,
  /** Messwert in Worten: „vor 2 Minuten“, „31 % frei“ */
  valueText: z.string(),
  /** Satz zur Lage – ohne Bucket- und Hostnamen */
  detail: z.string().nullable(),
  /** seit wann dieser Zustand gilt */
  since: zTimestamp.nullable(),
  checkedAt: zTimestamp.nullable(),
})

export const operationsStatus = defineContract({
  name: 'platform.operations.status',
  title: 'Datensicherung und Betrieb prüfen',
  description:
    'Zustand der Datensicherung (laufende Sicherung des Datenbank-Protokolls, Voll- und Tagessicherung, Rücksicherungsprobe, ' +
    'Speicherplatz, Zertifikat, Notfallpaket, Sicherung der Dateien) und des Archivs für das Finanzamt dieses Betriebs.',
  llm: {
    description:
      'Liefert den Zustand der Datensicherung und des Archivs: Gesamtzustand in einem Satz, je Prüfung Zustand und Messwert, ' +
      'Zeitraum, auf den sich die Datenbank zurücksetzen lässt, letzte Voll- und Tagessicherung, letzte Rücksicherungsprobe, ' +
      'Zahl der archivierten und noch offenen Archivobjekte dieses Betriebs. Verwenden für „Läuft die Datensicherung?“, ' +
      '„Wann wurde zuletzt gesichert?“, „Ist das Archiv in Ordnung?“. Ändert nichts.',
    keywords: ['Datensicherung', 'Backup', 'Sicherung', 'Archiv', 'Wiederherstellung', 'Rücksicherung', 'Speicherplatz', 'Zertifikat', 'Notfall'],
  },
  input: z.strictObject({}),
  output: z.object({
    overall: zOperationsState,
    /** „Datensicherung in Ordnung – zuletzt gesichert vor 2 Minuten.“ */
    line: z.string(),
    checks: z.array(zOperationsCheck),
    backup: z.object({
      /** der Sicherungsdienst meldet sich (Statusbericht vorhanden) */
      configured: z.boolean(),
      /** frühester und spätester Zeitpunkt für eine Rücksicherung */
      pitrFrom: zTimestamp.nullable(),
      pitrTo: zTimestamp.nullable(),
      lastFull: zTimestamp.nullable(),
      lastAny: zTimestamp.nullable(),
      /** „Hetzner Object Storage, Falkenstein“ – kein Bucket-Name */
      location: z.string().nullable(),
    }),
    probe: z.object({ lastAt: zTimestamp.nullable(), ok: z.boolean().nullable(), message: z.string().nullable() }),
    archive: z.object({
      configured: z.boolean(),
      /** Testbetrieb: kurze Sperre, Ablage unter test/ (nur mit einem Abnahme-Bucket) */
      testMode: z.boolean(),
      /** Echtbetriebsbeginn dieses Betriebs; null = nicht eingeschaltet */
      since: z.string().nullable(),
      objects: z.int().nonnegative(),
      bytes: z.int().nonnegative(),
      pending: z.int().nonnegative(),
      failed: z.int().nonnegative(),
      /** nicht archivierbar (z. B. Inhalt einer Belegdatei fehlt) – wird stündlich erneut versucht */
      blocked: z.int().nonnegative(),
      lastManifestDay: z.string().nullable(),
      byKind: z.array(z.object({ kind: z.string(), title: z.string(), objects: z.int().nonnegative(), retentionText: z.string() })),
    }),
  }),
  permission: 'platform.system.manage',
  risk: 'read',
  // „Läuft die Datensicherung?“ soll der Assistent beantworten können
  assistant: 'default',
})
